import { useQuery } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import { FetchCaseDocsRes, FetchCaseDocsCountRes } from '@/api/types/queries';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { MetadataFieldFilter } from '@/types';

export const useGetDocs = ({
  caseId,
  page,
  pageSize,
  searchQuery,
  processed,
  extensions,
  startDate,
  endDate,
  documentTypes,
  documentThemes,
  hasKeyFacts,
  hasRelevantFacts,
  runId,
  sortBy,
  sortOrder,
  metadataFilters,
}: {
  caseId: string | null;
  page: number;
  pageSize: number;
  searchQuery: string;
  processed?: boolean | null;
  extensions?: string[];
  startDate?: string | null;
  endDate?: string | null;
  documentTypes?: string[];
  documentThemes?: string[];
  hasKeyFacts?: boolean | null;
  hasRelevantFacts?: boolean | null;
  runId?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  metadataFilters?: MetadataFieldFilter[];
}) => {
  const { getFetchConfig } = useGetFetchConfig();

  return useQuery<FetchCaseDocsRes>(
    [
      QueryEntity.CaseDocs,
      { caseId },
      {
        page,
        pageSize,
        searchQuery,
        processed,
        extensions,
        startDate,
        endDate,
        sortBy,
        sortOrder,
        documentTypes,
        documentThemes,
        hasKeyFacts,
        hasRelevantFacts,
        runId,
        metadataFilters,
      },
    ],
    async () => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          page,
          pageSize,
          searchQuery: searchQuery || undefined,
          processed: processed ?? undefined,
          extensions: extensions && extensions.length > 0 ? extensions : undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          documentTypes: documentTypes && documentTypes.length > 0 ? documentTypes : undefined,
          documentThemes: documentThemes && documentThemes.length > 0 ? documentThemes : undefined,
          hasKeyFacts: hasKeyFacts ?? undefined,
          hasRelevantFacts: hasRelevantFacts ?? undefined,
          sortBy: sortBy || undefined,
          sortOrder: sortOrder || undefined,
          runId: runId || undefined,
          metadataFilters: metadataFilters && metadataFilters.length > 0 ? metadataFilters : undefined,
        },
      });
      const res = await fetch(`${APIBaseChronos}/api/case/${caseId}/docs/search`, fetchConfig);
      if (!res.ok) {
        throw new Error('Network response was not ok');
      }
      return res.json();
    },
    { refetchOnWindowFocus: false, enabled: !!caseId },
  );
};

export const useGetDocsCount = ({
  caseId,
  pageSize,
  searchQuery,
  processed,
  extensions,
  startDate,
  endDate,
  documentTypes,
  documentThemes,
  hasKeyFacts,
  hasRelevantFacts,
  metadataFilters,
  enabled = true,
}: {
  caseId: string | null;
  pageSize: number;
  searchQuery: string;
  processed?: boolean | null;
  extensions?: string[];
  startDate?: string | null;
  endDate?: string | null;
  documentTypes?: string[];
  documentThemes?: string[];
  hasKeyFacts?: boolean | null;
  hasRelevantFacts?: boolean | null;
  metadataFilters?: MetadataFieldFilter[];
  enabled?: boolean;
}) => {
  const { getFetchConfig } = useGetFetchConfig();

  return useQuery<FetchCaseDocsCountRes>(
    [
      QueryEntity.CaseDocsCount,
      { caseId },
      {
        pageSize,
        searchQuery,
        processed,
        extensions,
        startDate,
        endDate,
        documentTypes,
        documentThemes,
        hasKeyFacts,
        hasRelevantFacts,
        metadataFilters,
      },
    ],
    async () => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          pageSize,
          searchQuery: searchQuery || undefined,
          processed: processed ?? undefined,
          extensions: extensions && extensions.length > 0 ? extensions : undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          documentTypes: documentTypes && documentTypes.length > 0 ? documentTypes : undefined,
          documentThemes: documentThemes && documentThemes.length > 0 ? documentThemes : undefined,
          hasKeyFacts: hasKeyFacts ?? undefined,
          hasRelevantFacts: hasRelevantFacts ?? undefined,
          metadataFilters: metadataFilters && metadataFilters.length > 0 ? metadataFilters : undefined,
        },
      });
      const res = await fetch(`${APIBaseChronos}/api/case/${caseId}/docs/count`, fetchConfig);
      if (!res.ok) {
        throw new Error('Network response was not ok');
      }
      return res.json();
    },
    { refetchOnWindowFocus: false, enabled: !!caseId && enabled, staleTime: Infinity },
  );
};
